import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { WeightForm } from '@src/routes/members/components/body-measurements/form'
import { HistoryTable } from '@src/routes/members/components/body-measurements/table'
import { BodyMeasurementGoals } from '@src/routes/members/components/body-measurements/body-measurements-goals'
import { bulletColorMapping, lineColorMapping } from '@src/util/constants'
import { MetricData } from '@src/data/body-measurement'
import { Legend } from '@src/components/legend/legend'
import { Button } from '@src/elements/button/button'
import { BodyMeasurementsCard } from '@src/routes/members/components/body-measurements/body-measurements-card'
import { BodyMeasurementsService } from '@src/service/body-measurements'
import { Chart } from '@src/components/chart/chart'

import './weight-data.sass'
import { WeightUnit } from '@src/util/weight'

@Component({
  computed: {
    weightUnit () {
      return this.$store.state.club.weightUnit.toUpperCase()
    }
  }
})
export class WeightData extends Vue {

  protected static MEASUREMENT_TYPES = ['body_weight', 'body_fat', 'muscle_mass', 'body_water_mass', 'body_vfat']

  @Prop({ required: true })
  protected measurements: {
    bodyWeight: MetricData[],
    bodyFat: MetricData[],
    muscleMass: MetricData[],
    bodyWaterMass: MetricData[],
    bodyVfat: MetricData[]
  }

  @Prop({ required: true })
  protected bodyWeightNotes: string

  @Prop({ required: true })
  protected goals: any

  protected weightUnit: WeightUnit

  private selectedTab: 'measurements' | 'goals' = 'measurements'
  private isCardExpanded: boolean = false

  private selectedFilters: string[] = []

  constructor () {
    super()
    this.selectedFilters = Object.assign([], WeightData.MEASUREMENT_TYPES) || /* istanbul ignore next: Object.assign always returns the array */ []
  }

  expandCard () {
    this.isCardExpanded = !this.isCardExpanded
  }

  selectTab (tabName: 'measurements' | 'goals') {
    this.selectedTab = tabName
  }

  filter (measurementType) {
    let index = this.selectedFilters.indexOf(measurementType)
    if (index >= 0) {
      this.selectedFilters.splice(index, 1)
    } else {
      this.selectedFilters.push(measurementType)
    }
  }

  render (h: CreateElement) {
    return <div class='measurements-section weight-data'>
      <Subtitle1 class='title'>{this.$t('BODY_MEASUREMENTS.WEIGHT_DATA')}</Subtitle1>

      <div class='device-measurement-section-wrapper'>

        <Legend select={this.filter} items={
          WeightData.MEASUREMENT_TYPES.map(item => {
            return { type: item, label: this.$t(`BODY_MEASUREMENTS.WEIGHT.${item.toUpperCase()}`), color: bulletColorMapping[item], disabled: !this.selectedFilters.includes(item) }
          })
        } />

        <Chart chartId='weightDataChart' series={this.getChartSeries()}
               tooltipPointFormat={`<div>{point.y} ${this.$t(`UNITS.${this.weightUnit.toUpperCase()}`)}</div>`}
               emptyGraphMessage={this.$t('BODY_MEASUREMENTS.EMPTY_GRAPH.WEIGHT')} />

        <div class='cards-header-row row'>
          {this.isCardExpanded ? <div class={'cards-header-legend'}><Legend select={this.selectTab} items={[
            { type: 'measurements', label: this.$t('BODY_MEASUREMENTS.DATA_INPUT_OVERVIEW'), disabled: this.selectedTab !== 'measurements' },
            { type: 'goals', label: this.$t('BODY_MEASUREMENTS.GOALS'), disabled: this.selectedTab !== 'goals' }
          ]} /></div> : null}

          <Button type='remove' click={this.expandCard} icon={this.isCardExpanded ? 'util/caret-up' : 'util/caret-down'}
                  label={this.$t(this.isCardExpanded ? 'COLLAPSE' : 'EXPAND')} ref='expandBtn' class='expand-btn' />
        </div>

        {this.isCardExpanded ?
          this.selectedTab === 'measurements' ? this.renderCard(h) : this.renderGoalsCard(h) : null}
      </div>
    </div>
  }

  renderCard (h: CreateElement) {
    return <BodyMeasurementsCard type='weight'>
      <div slot='left'>
        <WeightForm notes={{ bodyWeightNotes: this.bodyWeightNotes }} />
      </div>
      <div slot='right'>
        <HistoryTable history={this.getTableHistory()}
                      bodyWeights={this.getBodyWeightsSorted()}
                      title={this.$t('BODY_MEASUREMENTS.WEIGHT.KNOWN_WEIGHT_DATA')} measuringUnit={this.$t(`UNITS.${this.weightUnit.toUpperCase()}`)} translationKey='BODY_MEASUREMENTS.WEIGHT.' type='weight'
                      legendItems={WeightData.MEASUREMENT_TYPES} />
      </div>
    </BodyMeasurementsCard>
  }

  renderGoalsCard (h: CreateElement) {
    let formData = { body_weight: null, body_fat: null, muscle_mass: null, body_water_mass: null, body_vfat: null }
    Object.keys(formData).map(key => (!this.goals[key]) ? this.goals[key] = [] : null)
    return <BodyMeasurementsCard type='weight-goals'>
      <BodyMeasurementGoals formData={formData}
      goals={this.goals} translationKey='BODY_MEASUREMENTS.WEIGHT.' type='weight-goals' />
    </BodyMeasurementsCard>
  }
  protected getBodyWeightsSorted () {
    return BodyMeasurementsService.convertToHistoryDTO(this.measurements.bodyWeight, 'body_weight') .slice().sort((a, b) => a.effectiveAt < b.effectiveAt ? -1 : a.effectiveAt > b.effectiveAt ? 1 : 0)
  }

  protected getChartSeries () {
    return [
      {
        data: this.selectedFilters.includes('body_weight') ? BodyMeasurementsService.convertToChartDTO(this.measurements.bodyWeight) : [],
        name: this.$t('BODY_MEASUREMENTS.WEIGHT.BODY_WEIGHT'),
        color: lineColorMapping['body_weight']
      }, {
        data: this.selectedFilters.includes('body_fat') ? BodyMeasurementsService.convertToChartDTO(this.measurements.bodyFat) : [],
        name: this.$t('BODY_MEASUREMENTS.WEIGHT.BODY_FAT'),
        color: lineColorMapping['body_fat']
      }, {
        data: this.selectedFilters.includes('muscle_mass') ? BodyMeasurementsService.convertToChartDTO(this.measurements.muscleMass) : [],
        name: this.$t('BODY_MEASUREMENTS.WEIGHT.MUSCLE_MASS'),
        color: lineColorMapping['muscle_mass']
      }, {
        data: this.selectedFilters.includes('body_water_mass') ? BodyMeasurementsService.convertToChartDTO(this.measurements.bodyWaterMass) : [],
        name: this.$t('BODY_MEASUREMENTS.WEIGHT.BODY_WATER_MASS'),
        color: lineColorMapping['body_water_mass']
      }, {
        data: this.selectedFilters.includes('body_vfat') ? BodyMeasurementsService.convertToChartDTO(this.measurements.bodyVfat) : [],
        name: this.$t('BODY_MEASUREMENTS.WEIGHT.BODY_VFAT'),
        color: lineColorMapping['body_vfat']
      }
    ]
  }

  protected getTableHistory () {
    return [...BodyMeasurementsService.convertToHistoryDTO(this.measurements.bodyWeight, 'body_weight'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.bodyFat, 'body_fat'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.muscleMass, 'muscle_mass'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.bodyWaterMass, 'body_water_mass'),
      ...BodyMeasurementsService.convertToHistoryDTO(this.measurements.bodyVfat, 'body_vfat')]
      .slice().sort((a, b) => a.effectiveAt > b.effectiveAt ? -1 : a.effectiveAt < b.effectiveAt ? 1 : 0)
  }
}
